import React, { memo, useCallback, useMemo, useState } from 'react';
import { FlyoutContainer, HeaderActionLink } from '@cmhc-schl/hearth-ui';
import { AppBar, Box, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { isServer } from '@sitecore-jss/sitecore-jss/utils';
import { Image } from '@sitecore-jss/sitecore-jss-react';
import { pathNames } from 'Constants/pathNames';
import HeaderComponentProps from 'Feature/Header/models/HeaderComponentProps';
import { useAuthenticationContext } from 'Foundation/Authentication';
import i18n from 'i18next';
import { useLocation } from 'react-router-dom';
import RoutableSitecoreLink from '../../../../RoutableSitecoreLink';
import HbtHeaderSideMenu from '../HbtHeaderSideMenu';
import HbtLanguageSwitcher from '../HbtLanguageSwitcher';
import styles from './styles.module.scss';

const HbtHeaderComponent = ({ fields }: HeaderComponentProps) => {
  const location = useLocation();

  const authenticationContext = useAuthenticationContext();
  const isApplicationOr500Error = !isServer()
    ? window.location.pathname.endsWith(pathNames.errorApplication) ||
      window.location.pathname.endsWith(pathNames.error500)
    : false;

  const theme = useTheme();
  const isMediumUp = useMediaQuery(theme.breakpoints.up('md'));

  const appBarStyles = useMemo(
    () =>
      `${styles.appBar} ${!isMediumUp ? styles.tabletBar : ''} ${
        !authenticationContext.isAuthenticated ? styles.transparentBar : ''
      }`,
    [authenticationContext.isAuthenticated, isMediumUp]
  );

  const toolbarStyles = useMemo(
    () =>
      `${styles.toolbar} ${
        !authenticationContext.isAuthenticated ? styles.rightContainerOnly : ''
      }`,
    [authenticationContext.isAuthenticated]
  );

  const [isActive, setIsActive] = useState(false);

  const notificationBtnClickHandler = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      if (e) {
        e.stopPropagation();
        setIsActive(!isActive);
      }
    },
    [isActive]
  );

  const [skipLinkHref, setSkipLinkHref] = useState('#maincontent');

  const handleSkipToContent = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const mainContentElement = document.querySelector(
        '[class*="styles_appLayout"] #maincontent'
      ) as HTMLElement;

      if (mainContentElement) {
        mainContentElement.classList.add(styles.noOutline);
        mainContentElement.focus();
      } else {
        e.currentTarget.blur();
      }
    },
    [location?.pathname]
  );

  return (
    <>
      {authenticationContext.isAuthenticated && (
        <AppBar
          role="banner"
          position="static"
          className={appBarStyles}
          aria-label="Global Header"
          id="header-app-bar"
        >
          <Toolbar disableGutters className={toolbarStyles}>
            <a onClick={handleSkipToContent} className={styles.skipLink} href={skipLinkHref}>
              <u>{fields?.skipContentLabel?.value ?? ''}</u>
            </a>
            {authenticationContext.isAuthenticated && (
              <Box className={styles.leftContainer}>
                <RoutableSitecoreLink link={fields.headerHomeLink} id="header-logo">
                  <div className={styles.logoTitleContainer}>
                    <Box className={styles.logoContainer}>
                      <Image field={fields.headerLogo} className={styles.headerLogoImage} />
                    </Box>
                    <div className={styles.textWrapper}>
                      <p className={styles.eyebrowHeaderText} id="eyebrow-header-text">
                        {fields.headerHeading.value}
                      </p>
                      <p className={styles.headerText} id="header-text">
                        {fields.headerSubTitle.value}
                      </p>
                    </div>
                  </div>
                </RoutableSitecoreLink>
              </Box>
            )}

            <Box className={styles.rightContainer}>
              <HbtLanguageSwitcher />
              {authenticationContext.isAuthenticated &&
                (!authenticationContext.displayTermsOfUseFlag ||
                  authenticationContext.isTermsAndConditionsAccepted() ||
                  isApplicationOr500Error) && (
                  <>
                    {!isApplicationOr500Error && (
                      <>
                        <HeaderActionLink
                          iconType="icon_notification_none"
                          isMediumUp={isMediumUp}
                          text={fields.notificationsText.value ?? 'Notifications'}
                          onToggle={notificationBtnClickHandler}
                        />
                        <FlyoutContainer
                          id="header-notification-flyout-container"
                          isHeaderRequired={true}
                          headerText={fields.notificationsText.value ?? 'Notifications'}
                          open={isActive}
                          onClose={() => setIsActive(false)}
                          content={
                            <div className={styles.noResultsWrapper}>
                              <p className={styles.noResultsText}>
                                {i18n.t(
                                  'Globals-Table-No-Search-To-Display',
                                  'No result to display'
                                )}
                              </p>
                            </div>
                          }
                        />
                      </>
                    )}
                    <HbtHeaderSideMenu
                      text={fields.menuText}
                      sideMenuItems={
                        !isApplicationOr500Error
                          ? (fields.sideMenuItems?.[0]?.fields?.subMenu ?? '')
                          : []
                      }
                      logoutLabel={fields.sideMenuItems?.[0]?.fields?.logoutLabel ?? ''}
                    />
                  </>
                )}
            </Box>
          </Toolbar>
        </AppBar>
      )}
    </>
  );
};

export default memo(HbtHeaderComponent);
